import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import routesConfig, { getTranslation } from '../../../Config/sidebarConfig';
import useTranslations from '../../../Hooks/useTranslations';
import translations from '../../../Config/translations.json';
import { selectIsBowlingRestaurant } from '../../../store/setupApi';
import useApi from '../../../Hooks/useApi';
import './css/secondaryTopBar.css';

const SecondaryTopBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const currentPath = location.pathname;
  const [activePath, setActivePath] = useState(null);
  const [newsletterEnabled, setNewsletterEnabled] = useState(false);
  const t = useTranslations(translations);
  const isBowling = useSelector(selectIsBowlingRestaurant);

  // Shift keys that have bowling variants
  const bowlingKeyMap = {
    'secondary.breakfast': 'secondary.breakfastBowling',
    'secondary.lunch': 'secondary.lunchBowling',
    'secondary.dinner': 'secondary.dinnerBowling',
  };

  // Helper to get button label from translation key
  const getButtonLabel = (button) => {
    if (button.labelKey) {
      const key = isBowling && bowlingKeyMap[button.labelKey]
        ? bowlingKeyMap[button.labelKey]
        : button.labelKey;
      return getTranslation(t, key, button.labelKey);
    }
    return button.label || button.path;
  };

  const findCurrentRoute = () => {
    const sortedRoutes = [...routesConfig].sort((a, b) => b.path.length - a.path.length);
    return sortedRoutes.find(route => currentPath.startsWith(route.path));
  };

  const currentRoute = findCurrentRoute();
  const rawTopBarConfig = currentRoute?.secondaryTopBar || [];
  const secondaryTopBarConfig = newsletterEnabled
    ? rawTopBarConfig
    : rawTopBarConfig.filter((tab) => tab.path !== '/email/newsletter');

  useEffect(() => {
    const baseDomain = window.baseDomain || '';
    api.get(`${baseDomain}api/auth-restaurant`)
      .then((data) => {
        setNewsletterEnabled(data?.['general-settings']?.newsletter_enabled === true);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (secondaryTopBarConfig.length > 0) {
      const matchingTab = secondaryTopBarConfig.find(tab => currentPath === tab.path);
      if (matchingTab) {
        setActivePath(matchingTab.path); // Set active tab based on current path
      } else if (!activePath) {
        setActivePath(secondaryTopBarConfig[0].path); // Default to the first tab
        navigate(secondaryTopBarConfig[0].path);
      }
    }
  }, [secondaryTopBarConfig, activePath, navigate, currentPath]);

  if (secondaryTopBarConfig.length === 0) {
    return null;
  }

  const handleNavigation = (path) => {
    setActivePath(path);
    navigate(path);
  };

  return (
    <div className="secondary-top-bar-component">
      <div className="secondary-top-bar">
        <div className="buttons-container">
          {secondaryTopBarConfig.map((button) => {
            const label = getButtonLabel(button);
            return (
              <motion.button
                key={button.path}
                className={clsx('secondary-button', {
                  'active': activePath === button.path,
                })}
                onClick={() => handleNavigation(button.path)}
                aria-label={label}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to={button.path} className="button-link">
                  {button.icon && <span className="button-icon">{button.icon}</span>}
                  <span className="button-label">{label}</span>
                  {activePath === button.path && (
                    <motion.div
                      layoutId="underline"
                      className="underline"
                      initial={false}
                      animate={{ opacity: 1, scaleX: 1 }}
                      exit={{ opacity: 0, scaleX: 0 }}
                    />
                  )}
                </Link>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SecondaryTopBar;
